import { format } from 'date-fns';
import { isCreationRecord, USAGE_ACTION } from '../constants/sample.constants';
import { formatUserName } from './name.util';
import { formatEntityDisplayName } from './softBreakIdentifier';
import { legacySampleTypeStringToCode } from './sampleTypeLabels';
import {
  compareUsageRecordsChronologically,
  recalculateRemainingQuantitiesPreservingOrder,
  resolveInitialQuantityFromRecords,
} from './sampleUsageRecordCalc';
import type { LastSampleInputData, SampleFormData } from './sampleFormSchema';
import { SAMPLE_DEFAULT_UNIT } from './sampleUnitLabels';
import { formatPathogenGenusDisplay } from './pathogenGenusDisplay';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

/** 新規作成向け sampleType（表示専用 pathogen → pathogen_stock） */
export function resolveCreateSampleType(raw: string | null | undefined): string {
  const coded = legacySampleTypeStringToCode(raw || '');
  if (coded) return coded;
  if (raw === 'pathogen') return 'pathogen_stock';
  return '';
}

/** 新規登録: 明示指定 or 病原体欄に値があるときだけ「含む」。未指定は短フォーム（含まない） */
export function resolveCreatePathogenOption(
  lastInput: Pick<
    LastSampleInputData,
    | 'pathogenOption'
    | 'pathogenGenus'
    | 'pathogenSpecies'
    | 'pathogenSubspecies'
    | 'pathogenStrain'
    | 'pathogenNote'
  >
): 'included' | 'excluded' {
  if (lastInput.pathogenOption === 'included' || lastInput.pathogenOption === 'excluded') {
    return lastInput.pathogenOption;
  }
  const hasFields = !!(
    lastInput.pathogenGenus ||
    lastInput.pathogenSpecies ||
    lastInput.pathogenSubspecies ||
    lastInput.pathogenStrain ||
    lastInput.pathogenNote
  );
  return hasFields ? 'included' : 'excluded';
}

export type SampleCreateLocationPrefill = {
  storageRoomId?: number;
  drawerNumber?: number | null;
  caseName?: string | null;
};

/** 編集モーダル投入用の使用履歴行（API → フォーム） */
export type SampleEditUsageRecordInput = {
  id?: number;
  usageDate: string;
  purpose?: string | null;
  quantity?: number;
  action?: string | null;
  remainingQuantity?: number | null;
  recordedBy?: string | null;
  user?: {
    id: number;
    username: string;
    firstName: string;
    lastName: string;
  };
};

export type SampleEditUsageRecordRow = {
  id?: number;
  clientKey: string;
  usageDate: string;
  action: string;
  purpose: string;
  quantity: number;
  remainingQuantity: number;
  recordedBy: string;
  isNew: boolean;
};

export type SampleEditSource = {
  name?: string | null;
  sampleType?: string | null;
  bslLevel?: string | null;
  initialQuantity?: number | null;
  remainingQuantity?: number | null;
  unit?: string | null;
  storageDate?: string | null;
  notes?: string | null;
  storageRoomId?: number | null;
  drawerNumber?: number | null;
  caseName?: string | null;
  pathogenGenus?: string | null;
  pathogenSpecies?: string | null;
  pathogenSubspecies?: string | null;
  pathogenStrain?: string | null;
  pathogenNote?: string | null;
  drawerNote?: string | null;
  createdAt?: string | null;
  usageRecords?: SampleEditUsageRecordInput[] | null;
};

export function createUsageRecordClientKey(
  record: { id?: number },
  randomId: () => string = () => crypto.randomUUID()
): string {
  if (record.id != null) return `id-${record.id}`;
  return `new-${randomId()}`;
}

/** 直前IDが現リストに無いとき sole room へフォールバック */
export function resolveCreateStorageRoomId(
  preferredRoomId: number | undefined,
  roomIds: ReadonlyArray<number>
): number | undefined {
  if (preferredRoomId != null && roomIds.includes(preferredRoomId)) {
    return preferredRoomId;
  }
  return roomIds.length === 1 ? roomIds[0] : undefined;
}

/** 新規登録モーダル用の初期値（直前入力＋位置プリフィル＋有効保管庫） */
export function buildSampleCreateFormData(
  lastInput: LastSampleInputData,
  options?: {
    createLocationPrefill?: SampleCreateLocationPrefill | null;
    availableRoomIds?: ReadonlyArray<number>;
    now?: Date;
  }
): SampleFormData {
  const createLocationPrefill = options?.createLocationPrefill;
  const now = options?.now ?? new Date();
  const preferredRoomId =
    createLocationPrefill?.storageRoomId ?? lastInput.storageRoomId;
  const roomIds = options?.availableRoomIds ?? [];
  const storageRoomId =
    roomIds.length > 0
      ? resolveCreateStorageRoomId(preferredRoomId, roomIds)
      : preferredRoomId;

  return {
    // 名前は毎回空（前回名の再利用は誤登録の温床）
    name: '',
    // 正規コードへ。シードの pathogen は作成用の既定 pathogen_stock に寄せる
    sampleType: resolveCreateSampleType(lastInput.sampleType),
    bslLevel: lastInput.bslLevel || '',
    initialQuantity:
      lastInput.initialQuantity !== undefined ? lastInput.initialQuantity : null,
    // 新規の残数量は登録数量に合わせる（前回値は引き継がない）
    remainingQuantity: null,
    unit: lastInput.unit?.trim() || SAMPLE_DEFAULT_UNIT,
    storageDate: format(now, "yyyy-MM-dd'T'HH:mm"),
    disposalDate: '',
    notes: '',
    storageRoomId,
    drawerNumber:
      createLocationPrefill?.drawerNumber !== undefined
        ? createLocationPrefill.drawerNumber
        : lastInput.drawerNumber !== undefined
          ? lastInput.drawerNumber
          : null,
    caseName: createLocationPrefill?.caseName ?? lastInput.caseName ?? '',
    pathogenOption: resolveCreatePathogenOption(lastInput),
    pathogenGenus: formatPathogenGenusDisplay(lastInput.pathogenGenus) || '',
    pathogenSpecies: lastInput.pathogenSpecies || '',
    pathogenSubspecies: lastInput.pathogenSubspecies || '',
    pathogenStrain: lastInput.pathogenStrain || '',
    pathogenNote: lastInput.pathogenNote || '',
    drawerNote: lastInput.drawerNote || '',
  };
}

function formatDatetimeLocal(value: string | Date): string {
  return format(new Date(value), "yyyy-MM-dd'T'HH:mm");
}

function recordedByFromUsageRecord(
  record: SampleEditUsageRecordInput,
  language: string
): string {
  if (!record.user) return record.recordedBy || '';
  const formatted = formatUserName(
    record.user.firstName,
    record.user.lastName,
    language
  );
  return formatted || record.user.username || record.recordedBy || '';
}

/** 編集時の使用履歴（作成レコード補完・並べ替え・残量再計算） */
export function buildSampleEditUsageRecords(
  sample: Pick<SampleEditSource, 'createdAt' | 'initialQuantity' | 'usageRecords'>,
  options: {
    language: string;
    randomId?: () => string;
  }
): SampleEditUsageRecordRow[] {
  const randomId = options.randomId ?? (() => crypto.randomUUID());
  const recordsToDisplay: SampleEditUsageRecordInput[] = sample.usageRecords?.length
    ? [...sample.usageRecords]
    : [];

  if (!recordsToDisplay.some((record) => isCreationRecord(record)) && sample.createdAt) {
    const firstRecord = recordsToDisplay[0] ?? null;
    recordsToDisplay.unshift({
      id: undefined,
      usageDate: formatDatetimeLocal(sample.createdAt),
      purpose: USAGE_ACTION.CREATED,
      quantity: 0,
      user: firstRecord?.user,
    });
  }

  const mappedRecords: SampleEditUsageRecordRow[] = [...recordsToDisplay]
    .sort(compareUsageRecordsChronologically)
    .map((record) => {
      const isCreation = isCreationRecord(record);
      return {
        id: record.id,
        clientKey: createUsageRecordClientKey(record, randomId),
        usageDate: formatDatetimeLocal(record.usageDate),
        action: isCreation ? USAGE_ACTION.CREATED : record.action || USAGE_ACTION.USE,
        purpose: isCreation ? USAGE_ACTION.CREATED : record.purpose || '',
        quantity: record.quantity || 0,
        remainingQuantity: isCreation
          ? (record.remainingQuantity ?? sample.initialQuantity ?? 0)
          : (record.remainingQuantity ?? 0),
        recordedBy: recordedByFromUsageRecord(record, options.language),
        isNew: false,
      };
    });

  const initialQuantity = resolveInitialQuantityFromRecords(
    mappedRecords,
    sample.initialQuantity ?? 0
  );
  return recalculateRemainingQuantitiesPreservingOrder(
    mappedRecords,
    initialQuantity,
    (record) => record.clientKey
  );
}

/** 編集モーダル用のフォーム初期値 */
export function buildSampleEditFormData(
  sample: SampleEditSource,
  t?: TranslateFn
): SampleFormData {
  const hasPathogen = !!(sample.pathogenGenus || sample.pathogenSpecies);
  return {
    // 一覧と同じ表示名。送信時は resolveSampleNameForSubmit で生名を復元
    name: formatEntityDisplayName(sample.name || '', t),
    sampleType:
      legacySampleTypeStringToCode(sample.sampleType || '') || sample.sampleType || '',
    bslLevel: sample.bslLevel || '',
    initialQuantity: sample.initialQuantity ?? null,
    remainingQuantity: sample.remainingQuantity ?? null,
    unit: sample.unit?.trim() || SAMPLE_DEFAULT_UNIT,
    storageDate: sample.storageDate ? formatDatetimeLocal(sample.storageDate) : '',
    disposalDate: '',
    notes: sample.notes || '',
    storageRoomId: sample.storageRoomId || undefined,
    drawerNumber: sample.drawerNumber ?? null,
    caseName: sample.caseName || '',
    pathogenOption: hasPathogen ? 'included' : 'excluded',
    pathogenGenus: formatPathogenGenusDisplay(sample.pathogenGenus) || '',
    pathogenSpecies: sample.pathogenSpecies || '',
    pathogenSubspecies: sample.pathogenSubspecies || '',
    pathogenStrain: sample.pathogenStrain || '',
    pathogenNote: sample.pathogenNote || '',
    drawerNote: sample.drawerNote || '',
  };
}

/** 編集モーダル一式（フォーム＋使用履歴＋派生状態） */
export function buildSampleEditInit(
  sample: SampleEditSource,
  options: { language: string; randomId?: () => string; t?: TranslateFn }
) {
  const formData = buildSampleEditFormData(sample, options.t);
  return {
    formData,
    pathogenOption: formData.pathogenOption,
    sampleCreatedDate: sample.createdAt ? formatDatetimeLocal(sample.createdAt) : '',
    usageRecords: buildSampleEditUsageRecords(sample, options),
    storageRoomId: sample.storageRoomId ?? undefined,
  };
}
